
import { FilterParam, IColumnSetting, Sort } from "models/grids";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { ReactNode, createContext, useCallback, useEffect, useMemo, useState } from "react";
import { GridFilterItem } from "@mui/x-data-grid-pro";

export enum PreferenceType {
    MyProjectsDashboard = 1,
    AllProjectsDashboard = 2,
    ProjectOverview = 3,
    ProjectMyTasks = 4,
    ProjectTasksGrid = 5,
    MyTeamWorkItemDashboard = 6,
    MyTeamTaskDashboard = 7,
    MyWorkItemsDashboard = 8,
    TaskDependencies = 9,  
    TaskAttachmentsGrid = 10,
    AllWorkItemsDashboard = 11,
    WorkItemMyTasks = 12,
    WorkItemAttachmentsGrid = 13,
    WorkItemRelatedWorkItems = 14,
    WorkItemOverviewTasks = 15
}

export interface IDashboardSettings {
    gridSettings?: IColumnSetting[];
    defaultTab?: string;
    pageSize?: number;
    columnDensity?: string;
    defaultFilter?: GridFilterItem;
    sort?: Sort[];
    filterParam?: FilterParam;
}

export interface IPreference {
    id: number;
    settings: IDashboardSettings;
}

export interface IPreferenceContainer {
    preferences: IPreference[]
}

type GetPreferenceSignature = (type: PreferenceType) => IPreference | null;

export interface IPreferencesContext {
    loaded: boolean,
    data: IPreferenceContainer,
    setAllPreferences: (preferences: IPreferenceContainer) => void,
    getPreference: GetPreferenceSignature,
    setPreference: (preference: IPreference) => void
}

const PreferencesContext = createContext<IPreferencesContext | null>(null);
const PreferencesConsumer = PreferencesContext.Consumer;

const PreferencesProvider = (props: { children: ReactNode }) => {
    
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const [ _preferencesToSave, _setPreferencesToSave ] = useState<IPreferenceContainer>({
        preferences: []
    });

    const [ loaded, setLoaded ] = useState<boolean>(false);
    const [ data, setData ] = useState<IPreferenceContainer>({
        preferences: []
    });

    const setAllPreferences = useCallback((preferences: IPreferenceContainer) => {
        if (loaded === false) return;
        _setPreferencesToSave(preferences);
    }, [ loaded ]);

    const setPreference = useCallback((preference: IPreference) => {        
        if (loaded === false) return;
        
        const newPreferences = data;
        const settingIndex = newPreferences.preferences.findIndex(p => p.id == preference.id);

        if (settingIndex !== -1) {
            newPreferences.preferences[settingIndex] = preference;
        } else {
            newPreferences.preferences.push(preference);
        }

        _setPreferencesToSave(newPreferences);
    }, [ loaded, data ]);

    const getPreference: GetPreferenceSignature = useCallback((type) => {
        if (loaded === false) return null;

        const preferenceIndex = data.preferences.findIndex(p => p.id == type);
        if (preferenceIndex !== -1) {
            return data.preferences[preferenceIndex];
        }

        return null;
    }, [ loaded, data ]);

    const value = useMemo(() => ({
        loaded,
        data,
        setAllPreferences,
        getPreference,
        setPreference
    }), [ loaded, data ]);

    const savePreferences = async (signal: AbortSignal) => {
        try {
            const resp = await fetcher(`/api/v3/preferences/my`, {
                method: 'PUT',
                body: JSON.stringify(_preferencesToSave),
                signal: signal
            });

            if (!resp.ok) {
                // FAIL STATE
            }

            _setPreferencesToSave({
                preferences: []
            });
        } catch (err) {

        } 
    };
    
    const loadPreferences = async (signal: AbortSignal) => {
        try {
            const resp = await fetcher(`/api/v3/preferences/my`, {                        
                signal: signal
            });

            if (!resp.ok) {
                // FAIL STATE
            }

            const data = await resp.json() as IPreferenceContainer;
            setLoaded(true);
            setData(data);
        } catch (err) {

        }
    };

    useEffect(() => {        
        const controller = new AbortController();
        if (user && user.id_token) {
            loadPreferences(controller.signal);
        }

        return () => controller.abort();
    }, [ user ]);

    useEffect(() => {
        if (loaded === false) return;
        if (_preferencesToSave.preferences.length === 0) return;

        const controller = new AbortController();
        if (user && user.id_token) {
            savePreferences(controller.signal);
        }

        return () => {
            controller.abort();
        }
    }, [ user, loaded, _preferencesToSave ]);

    return <PreferencesContext.Provider value={value}>
        {props.children}
    </PreferencesContext.Provider>
};

export { 
    PreferencesContext,
    PreferencesConsumer, 
    PreferencesProvider 
};